import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

class PdpSizeDrawerComponent extends DialogComponent {
  requiredRefs = ['dialog', 'confirmButton'];

  /** @type {string | null} */
  #confirmedVariantId = null;

  /** @type {string | null} */
  #confirmedOptionValueId = null;

  /** @type {boolean} */
  #isPending = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#syncExternalSizeLabelFromSource();
    this.#resetSelectionState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  showDialog() {
    this.#resetSelectionState();
    super.showDialog();
  }

  /**
   * @param {Event} event
   */
  handleOptionClick(event) {
    const button =
      event.currentTarget instanceof HTMLButtonElement
        ? event.currentTarget
        : event.target instanceof HTMLElement
        ? event.target.closest('[data-size-drawer-option]')
        : null;

    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    this.#confirmedVariantId = button.dataset.variantId || null;
    this.#confirmedOptionValueId = button.dataset.optionValueId || null;
    this.#syncExternalSizeLabel(button.dataset.optionLabel || button.textContent?.trim() || '');

    this.querySelectorAll('[data-size-drawer-option]').forEach((optionButton) => {
      if (!(optionButton instanceof HTMLButtonElement)) return;

      const isSelected = optionButton === button;
      if (isSelected) {
        optionButton.setAttribute('data-selected', 'true');
      } else {
        optionButton.removeAttribute('data-selected');
      }
      optionButton.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    this.refs.confirmButton.disabled = !this.#confirmedVariantId || !this.#confirmedOptionValueId;
  }

  /**
   * @param {Event} event
   */
  async handleConfirmClick(event) {
    event.preventDefault();

    if (!this.#confirmedVariantId || !this.#confirmedOptionValueId) return;

    const formId = this.dataset.productFormId;
    if (!formId) return;

    const form = document.getElementById(formId);
    if (!(form instanceof HTMLFormElement)) return;

    this.#syncHiddenSizeSource();

    const variantIdInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));
    if (variantIdInput) {
      variantIdInput.value = this.#confirmedVariantId;
    }

    const productFormComponent = this.#getProductFormComponent(form);
    if (productFormComponent) {
      productFormComponent.dataset.deferCartDrawerOpen = 'true';
    }

    this.#setPendingState(true);
    this.refs.confirmButton.disabled = true;
    form.requestSubmit();
  }

  #resetSelectionState() {
    this.#setPendingState(false);
    this.#confirmedVariantId = null;
    this.#confirmedOptionValueId = null;

    this.querySelectorAll('[data-size-drawer-option]').forEach((optionButton) => {
      if (!(optionButton instanceof HTMLButtonElement)) return;

      optionButton.removeAttribute('data-selected');
      optionButton.setAttribute('aria-pressed', 'false');
    });

    if (this.refs.confirmButton) {
      this.refs.confirmButton.disabled = true;
    }
  }

  #handleCartAdd = async (event) => {
    if (!this.#isPending) return;

    const productFormComponent = this.#getCurrentProductFormComponent();
    if (!productFormComponent || event.target !== productFormComponent) return;
    if (!event.detail.data?.deferDrawerOpen) return;

    if (event.detail.data?.didError) {
      this.#setPendingState(false);
      this.refs.confirmButton.disabled = !this.#confirmedVariantId || !this.#confirmedOptionValueId;
      return;
    }

    const cartDrawer = document.querySelector('cart-drawer-component');

    this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
    await this.closeDialog();
    this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');

    this.#setPendingState(false);

    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
  };

  #syncHiddenSizeSource() {
    if (!this.#confirmedOptionValueId) return;

    const variantPicker = this.closest('variant-picker');
    if (!variantPicker) return;

    const sourceInputs = Array.from(
      variantPicker.querySelectorAll('.variant-option--size-source input[type="radio"][data-option-value-id]')
    );

    sourceInputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const isSelected = input.dataset.optionValueId === this.#confirmedOptionValueId;
      input.checked = isSelected;
      input.dataset.currentChecked = isSelected ? 'true' : 'false';
      input.dataset.previousChecked = 'false';
    });
  }

  #syncExternalSizeLabel(label) {
    const host = this.closest('[data-pdp-mobile-tray-host]');
    if (!(host instanceof HTMLElement) || !label) return;

    host.querySelectorAll('[data-pdp-mobile-size-current]').forEach((node) => {
      node.textContent = label;
    });
  }

  #syncExternalSizeLabelFromSource() {
    const variantPicker = this.closest('variant-picker');
    if (!(variantPicker instanceof HTMLElement)) return;

    const selectedInput = variantPicker.querySelector(
      '.variant-option--size-source input[type="radio"][data-current-checked="true"], .variant-option--size-source input[type="radio"]:checked'
    );

    if (!(selectedInput instanceof HTMLInputElement)) return;

    this.#syncExternalSizeLabel(selectedInput.dataset.optionLabel || selectedInput.value || '');
  }

  #getCurrentProductFormComponent() {
    const formId = this.dataset.productFormId;
    if (!formId) return null;

    const form = document.getElementById(formId);
    if (!(form instanceof HTMLFormElement)) return null;

    return this.#getProductFormComponent(form);
  }

  #getProductFormComponent(form) {
    const productFormComponent = form.closest('product-form-component');

    return productFormComponent instanceof HTMLElement ? productFormComponent : null;
  }

  #setPendingState(isPending) {
    this.#isPending = isPending;
    this.toggleAttribute('data-pending', isPending);

    if (!this.refs.confirmButton) return;

    this.refs.confirmButton.toggleAttribute('data-loading', isPending);
    this.refs.confirmButton.setAttribute('aria-busy', isPending ? 'true' : 'false');
    this.refs.confirmButton.textContent = isPending ? 'Adding...' : 'Add to bag';
  }
}

if (!customElements.get('pdp-size-drawer-component')) {
  customElements.define('pdp-size-drawer-component', PdpSizeDrawerComponent);
}
